import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Truck, 
  Package, 
  Clock,
  Users,
  MapPin,
  Calendar,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  PieChart,
  Activity,
  Target,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Info
} from 'lucide-react';

// Sample data for the mockup
const mockData = {
  revenue: {
    current: 47250,
    previous: 42800,
    change: 10.4
  },
  collections: {
    rate: 94.2,
    outstanding: 8450,
    overdue: 2150
  },
  fleet: {
    total: 85,
    onRent: 62,
    inYard: 18,
    maintenance: 5,
    utilization: 72.9
  },
  routes: {
    avgStops: 14.2,
    avgTime: "6h 24m",
    efficiency: 87,
    milesPerDay: 142
  },
  service: {
    onTime: 96.4,
    avgResponseTime: "2.4 hrs",
    issuesResolved: 98.1,
    customerSatisfaction: 4.7
  }
};

function StatCard({ title, value, change, changeType, icon: Icon, subtitle, prefix = '', suffix = '' }) {
  const isPositive = changeType === 'positive' || (changeType === 'auto' && change > 0);
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;
  const trendColor = isPositive ? 'text-green-600' : 'text-red-600';
  const trendBg = isPositive ? 'bg-green-50' : 'bg-red-50';
  
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">
              {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
            </p>
            {subtitle && (
              <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
            )}
          </div>
          <div className="p-2 bg-[#194461]/10 rounded-lg">
            <Icon className="h-5 w-5 text-[#194461]" />
          </div>
        </div>
        {change !== undefined && (
          <div className={`inline-flex items-center gap-1 mt-3 px-2 py-1 rounded-full text-xs font-medium ${trendBg} ${trendColor}`}>
            <TrendIcon size={12} />
            <span>{Math.abs(change)}% vs last month</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MockChart({ type, title, height = 200 }) {
  // Simple SVG mockup charts
  if (type === 'bar') {
    return (
      <div className="relative" style={{ height }}>
        <div className="absolute inset-0 flex items-end justify-around px-4 pb-6">
          {[65, 45, 78, 52, 88, 67, 72, 58, 82, 70, 75, 90].map((h, i) => (
            <div 
              key={i} 
              className="w-6 bg-gradient-to-t from-[#194461] to-[#52B8E1] rounded-t transition-all hover:opacity-80"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
        <div className="absolute bottom-0 left-0 right-0 flex justify-around text-[10px] text-slate-400 px-4">
          {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(m => (
            <span key={m}>{m}</span>
          ))}
        </div>
      </div>
    );
  }
  
  if (type === 'line') {
    return (
      <div className="relative" style={{ height }}>
        <svg className="w-full h-full" viewBox="0 0 400 150" preserveAspectRatio="none">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#52B8E1" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#52B8E1" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M 0 120 Q 50 100 100 90 T 200 70 T 300 50 T 400 30"
            fill="none"
            stroke="#194461"
            strokeWidth="3"
          />
          <path
            d="M 0 120 Q 50 100 100 90 T 200 70 T 300 50 T 400 30 L 400 150 L 0 150 Z"
            fill="url(#lineGradient)"
          />
        </svg>
      </div>
    );
  }
  
  if (type === 'donut') {
    const segments = [
      { percent: 73, color: '#194461', label: 'On Rent' },
      { percent: 21, color: '#52B8E1', label: 'In Yard' },
      { percent: 6, color: '#f59e0b', label: 'Maintenance' }
    ];
    let currentAngle = -90;
    
    return (
      <div className="relative flex items-center justify-center" style={{ height }}>
        <svg className="w-32 h-32" viewBox="0 0 100 100">
          {segments.map((seg, i) => {
            const angle = (seg.percent / 100) * 360;
            const largeArc = angle > 180 ? 1 : 0;
            const startX = 50 + 40 * Math.cos((currentAngle * Math.PI) / 180);
            const startY = 50 + 40 * Math.sin((currentAngle * Math.PI) / 180);
            currentAngle += angle;
            const endX = 50 + 40 * Math.cos((currentAngle * Math.PI) / 180);
            const endY = 50 + 40 * Math.sin((currentAngle * Math.PI) / 180);
            
            return (
              <path
                key={i}
                d={`M 50 50 L ${startX} ${startY} A 40 40 0 ${largeArc} 1 ${endX} ${endY} Z`}
                fill={seg.color}
                className="hover:opacity-80 transition-opacity"
              />
            );
          })}
          <circle cx="50" cy="50" r="25" fill="white" />
        </svg>
        <div className="absolute text-center">
          <p className="text-2xl font-bold text-slate-900">73%</p>
          <p className="text-xs text-slate-500">Utilized</p>
        </div>
        <div className="absolute right-0 top-1/2 -translate-y-1/2 space-y-2">
          {segments.map((seg, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: seg.color }} />
              <span className="text-slate-600">{seg.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return null;
}

function InsightCard({ icon: Icon, title, value, description, trend, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    red: 'bg-red-50 text-red-600 border-red-200'
  };
  
  return (
    <div className={`p-4 rounded-lg border ${colors[color]}`}>
      <div className="flex items-start gap-3">
        <Icon size={20} className="mt-0.5" />
        <div>
          <p className="font-medium">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          <p className="text-sm opacity-80 mt-1">{description}</p>
        </div>
      </div>
    </div>
  );
}

export default function Analytics() {
  return (
    <AdminLayout title="Analytics">
      {/* Coming Soon Banner */}
      <div className="mb-6 p-4 bg-gradient-to-r from-[#194461] to-[#2a5a7a] rounded-xl text-white flex items-center gap-4">
        <div className="p-3 bg-white/10 rounded-lg">
          <BarChart3 size={24} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold">Analytics Dashboard Preview</h3>
          <p className="text-sm text-white/80">
            This is a preview of the analytics capabilities. Full functionality coming soon with your live data.
          </p>
        </div>
        <Badge variant="outline" className="bg-white/10 text-white border-white/20">
          Preview Mode
        </Badge>
      </div>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Monthly Revenue"
          value={mockData.revenue.current}
          prefix="$"
          change={mockData.revenue.change}
          changeType="positive"
          icon={DollarSign}
          subtitle="Invoiced this month"
        />
        <StatCard
          title="Fleet Utilization"
          value={mockData.fleet.utilization}
          suffix="%"
          change={3.2}
          changeType="positive"
          icon={Truck}
          subtitle={`${mockData.fleet.onRent} of ${mockData.fleet.total} units on rent`}
        />
        <StatCard
          title="On-Time Service"
          value={mockData.service.onTime}
          suffix="%"
          change={1.8}
          changeType="positive"
          icon={Clock}
          subtitle="Services completed on schedule"
        />
        <StatCard
          title="Collection Rate"
          value={mockData.collections.rate}
          suffix="%"
          change={-0.5}
          changeType="auto"
          icon={CheckCircle}
          subtitle={`$${mockData.collections.outstanding.toLocaleString()} outstanding`}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity size={18} className="text-[#52B8E1]" />
              Revenue Trend
            </CardTitle>
            <CardDescription>Monthly revenue over the past year</CardDescription>
          </CardHeader>
          <CardContent>
            <MockChart type="bar" height={220} />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PieChart size={18} className="text-[#52B8E1]" />
              Fleet Status
            </CardTitle>
            <CardDescription>Current unit allocation</CardDescription>
          </CardHeader>
          <CardContent>
            <MockChart type="donut" height={180} />
          </CardContent>
        </Card>
      </div>

      {/* Operational Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin size={18} className="text-[#52B8E1]" />
              Route Efficiency
            </CardTitle>
            <CardDescription>Key routing performance metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Avg Stops/Route</p>
                <p className="text-2xl font-bold text-slate-900">{mockData.routes.avgStops}</p>
                <div className="flex items-center gap-1 text-green-600 text-xs mt-1">
                  <ArrowUpRight size={12} />
                  <span>+2.1 vs last month</span>
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Avg Route Time</p>
                <p className="text-2xl font-bold text-slate-900">{mockData.routes.avgTime}</p>
                <div className="flex items-center gap-1 text-green-600 text-xs mt-1">
                  <ArrowDownRight size={12} />
                  <span>-18 min vs last month</span>
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Route Efficiency</p>
                <p className="text-2xl font-bold text-slate-900">{mockData.routes.efficiency}%</p>
                <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                  <div className="bg-[#194461] h-2 rounded-full" style={{ width: `${mockData.routes.efficiency}%` }} />
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Miles/Day</p>
                <p className="text-2xl font-bold text-slate-900">{mockData.routes.milesPerDay}</p>
                <p className="text-xs text-slate-400 mt-1">Fleet average</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users size={18} className="text-[#52B8E1]" />
              Driver Performance
            </CardTitle>
            <CardDescription>Team productivity rankings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { name: 'Mike Johnson', stops: 156, efficiency: 94, trend: 'up' },
                { name: 'Dave Wilson', stops: 148, efficiency: 91, trend: 'up' },
                { name: 'Tom Anderson', stops: 142, efficiency: 88, trend: 'down' },
                { name: 'Chris Martinez', stops: 138, efficiency: 86, trend: 'up' }
              ].map((driver, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#194461] text-white flex items-center justify-center text-sm font-medium">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{driver.name}</p>
                    <p className="text-xs text-slate-500">{driver.stops} stops this month</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-900">{driver.efficiency}%</p>
                    <p className={`text-xs ${driver.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                      {driver.trend === 'up' ? '↑' : '↓'} efficiency
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Business Insights */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Zap size={18} className="text-[#52B8E1]" />
            Business Insights
          </CardTitle>
          <CardDescription>AI-powered recommendations based on your data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <InsightCard
              icon={Target}
              title="Revenue Opportunity"
              value="$3,200"
              description="5 units sitting idle could generate this monthly"
              color="green"
            />
            <InsightCard
              icon={Clock}
              title="Time Savings"
              value="4.2 hrs/week"
              description="Potential savings with optimized routing"
              color="blue"
            />
            <InsightCard
              icon={AlertTriangle}
              title="At-Risk Customers"
              value="3 accounts"
              description="Showing reduced order frequency"
              color="amber"
            />
            <InsightCard
              icon={TrendingUp}
              title="Peak Demand"
              value="March-May"
              description="Construction season - plan fleet expansion"
              color="blue"
            />
          </div>
        </CardContent>
      </Card>

      {/* Financial Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign size={18} className="text-[#52B8E1]" />
              Accounts Receivable Aging
            </CardTitle>
            <CardDescription>Outstanding invoice breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { label: 'Current (0-30 days)', amount: 12450, percent: 58, color: 'bg-green-500' },
                { label: '31-60 days', amount: 5200, percent: 24, color: 'bg-yellow-500' },
                { label: '61-90 days', amount: 2800, percent: 13, color: 'bg-orange-500' },
                { label: '90+ days', amount: 1050, percent: 5, color: 'bg-red-500' }
              ].map((item, i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">{item.label}</span>
                    <span className="font-medium">${item.amount.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3">
                    <div className={`${item.color} h-3 rounded-full transition-all`} style={{ width: `${item.percent}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t flex justify-between">
              <span className="font-medium text-slate-900">Total Outstanding</span>
              <span className="font-bold text-xl text-slate-900">$21,500</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar size={18} className="text-[#52B8E1]" />
              Upcoming Renewals
            </CardTitle>
            <CardDescription>Rentals ending this month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { customer: 'ABC Construction', units: 4, date: 'Mar 15', value: '$600/wk' },
                { customer: 'Festival Events', units: 8, date: 'Mar 18', value: '$1,200/wk' },
                { customer: 'Metro Builders', units: 2, date: 'Mar 22', value: '$300/wk' },
                { customer: 'City Projects', units: 6, date: 'Mar 28', value: '$900/wk' }
              ].map((item, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-slate-900">{item.customer}</p>
                      <p className="text-xs text-slate-500">{item.units} units • Ends {item.date}</p>
                    </div>
                    <Badge variant="outline" className="text-[#194461] border-[#194461]/30">
                      {item.value}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="outline" className="w-full mt-4" size="sm">
              View All Renewals
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Footer Note */}
      <div className="text-center py-8 text-slate-500">
        <Info size={20} className="mx-auto mb-2 text-slate-400" />
        <p className="text-sm">
          This analytics dashboard shows sample data for demonstration purposes.
          <br />
          Connect your live data to unlock full analytics capabilities.
        </p>
      </div>
    </AdminLayout>
  );
}
